import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePeerTube } from '../contexts/PeerTubeContext';
import { fetchVideo, getVideoEmbedUrl, getFullThumbnailUrl } from '../services/api';
import { 
  Box, 
  TextField, 
  Typography, 
  IconButton, 
  useTheme, 
  useMediaQuery,
  Collapse,
  Divider,
  GlobalStyles,
  Avatar,
  Tooltip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
// Phila Markdown is loaded from CDN in index.html
import tubeAILogo from '/tubeAI.svg';
import SaveMessageButton from './SaveMessageButton';
import BucketButton from './BucketButton';
import { ChatMessage, VideoInfo, SenderType } from '../types/types';

// Using types from types.ts

// Import the Lyra API service
import { createChat, streamQuestion } from '../services/lyraApi';

// Add TypeScript declaration for PhilaMarkdown if not already declared
declare global {
  interface Window {
    PhilaMarkdown: any;
  }
}

const VideoPlayer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedInstance } = usePeerTube();
  
  // State for storing the chat ID
  const [chatId, setChatId] = useState<string>('');
  
  const [infoExpanded, setInfoExpanded] = useState(false);
  // Always keep chat expanded regardless of device
  const chatExpanded = true;
  
  const [video, setVideo] = useState<VideoInfo>({
    name: 'Lade Video...',
    duration: 0,
    uuid: id,
    displayName: 'Unbekannt'
  });
  
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 1,
      text: "Hallo! Ich bin Ihr Video-Assistent. Wie kann ich Ihnen mit diesem Video helfen?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  
  const [inputMessage, setInputMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const philaMarkdownRef = useRef<any>(null);
  
  // Initialize Phila Markdown when component mounts
  useEffect(() => {
    if (window.PhilaMarkdown) {
      philaMarkdownRef.current = new window.PhilaMarkdown();
    }
  }, []);
  
  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Initialize chat ID on component mount
  useEffect(() => {
    const initializeChat = async () => {
      try {
        const newChatId = await createChat();
        setChatId(newChatId);
        console.log('Chat initialized with ID:', newChatId);
      } catch (error) {
        console.error('Failed to initialize chat:', error);
      }
    };

    initializeChat();
  }, []);
  
  // Fetch video data
  useEffect(() => {
    const fetchVideoData = async () => {
      try {
        const data = await fetchVideo(selectedInstance.apiUrl, id || '');
        setVideo(prev => ({
          ...prev,
          name: data.name,
          description: data.description,
          duration: data.duration,
          thumbnailPath: data.thumbnailPath,
          channel: data.channel,
          account: data.account,
          views: data.views,
          likes: data.likes,
          uuid: data.uuid || id // Ensure uuid is set
        }));
      } catch (error) {
        // Handle error gracefully without console logging in production
        setVideo(prev => ({
          ...prev,
          name: 'Video konnte nicht geladen werden',
          description: 'Es gab ein Problem beim Laden des Videos.',
          uuid: id
        }));
      }
    };

    if (id) {
      fetchVideoData();
    }
  }, [id, selectedInstance.apiUrl]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputMessage(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim() || sendingMessage) return;

    const currentInput = inputMessage.trim();
    setInputMessage('');
    setSendingMessage(true);

    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now(),
      text: currentInput,
      sender: 'user' as SenderType,
      timestamp: new Date()
    };
    
    // Safely add user message
    setMessages(prev => [...prev, userMessage]);

    // Add typing indicator message to show when waiting for webhook response
    const typingIndicatorId = Date.now() + 1;
    const typingIndicator: ChatMessage = {
      id: typingIndicatorId,
      text: "",
      sender: 'bot',
      timestamp: new Date(),
      isTypingIndicator: true
    };
    
    // Safely add typing indicator
    setMessages(prev => [...prev, typingIndicator]);

    try {
      // Create initial streaming message with empty text immediately
      const messageId = Date.now() + 1;
      const initialStreamingMessage: ChatMessage = {
        id: messageId,
        text: '',
        fullText: '', // Will be built up as we receive chunks
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: true
      };

      // Replace typing indicator with initial streaming message immediately
      setMessages(prev => {
        // Make sure we're not duplicating messages
        const filtered = prev.filter(msg => msg.id !== typingIndicatorId);
        return [...filtered, initialStreamingMessage];
      });

      // Full response text that will be built up as we receive chunks
      let fullResponseText = '';

      // Function to handle each chunk of the streaming response
      const handleStreamChunk = (chunk: string) => {
        // Append the chunk to the full response text
        fullResponseText += chunk;
        
        // Update the message with the current text
        setMessages(prev => {
          return prev.map(msg => {
            if (msg.id === messageId) {
              return {
                ...msg,
                text: fullResponseText,
                fullText: fullResponseText,
                isStreaming: true
              };
            }
            return msg;
          });
        });
      };

      // Function to send webhook request and get the response
      const sendWebhookRequest = async (): Promise<string> => {
        try {
          const controller = new AbortController();
          const signal = controller.signal;
          
          const fetchPromise = fetch('https://n8n.companion.at/webhook/7d68a549-1cf6-44ff-9f92-7457c842581a', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            signal,
            body: JSON.stringify({
              workflow_data: {
                message: currentInput,
                userQuery: currentInput,
                videoId: video.uuid,
                videoContext: {
                  videoId: video.uuid,
                  title: video.name,
                  description: video.description || '',
                  uploader: video.account?.displayName || '',
                  channel: video.channel?.displayName || '',
                  publishDate: new Date().toISOString(),
                  views: video.views || 0,
                  likes: video.likes || 0,
                  instance: selectedInstance.name,
                  instanceUrl: selectedInstance.url
                }
              },
              body: {
                instanceUrl: selectedInstance.url,
                videoId: video.uuid
              }
            })
          });
          
          // Create a timeout promise
          const timeoutPromise = new Promise<Response>((_, reject) => {
            const timeoutId = setTimeout(() => {
              controller.abort();
              reject(new Error('Request timed out'));
            }, 10000); // Shorter timeout (10s) to ensure we don't wait too long
            
            // Clean up the timeout if fetchPromise resolves or rejects
            fetchPromise.then(() => clearTimeout(timeoutId), () => clearTimeout(timeoutId));
          });
          
          // Race between the fetch and the timeout
          const response = await Promise.race([fetchPromise, timeoutPromise]);
          
          if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`);
            return '';
          }
          
          const responseText = await response.text();
          console.log('Webhook response:', responseText);
          
          // If we get a response, extract it
          if (responseText && responseText.trim() !== '') {
            try {
              const responseData = JSON.parse(responseText);
              let webhookResponse = '';
              
              // Extract response from different possible formats
              if (Array.isArray(responseData) && responseData.length > 0) {
                const firstItem = responseData[0];
                if (typeof firstItem === 'string') {
                  webhookResponse = firstItem;
                } else if (firstItem && typeof firstItem === 'object') {
                  webhookResponse = firstItem.output || firstItem.message || firstItem.text || firstItem.response || '';
                }
              } else if (responseData && typeof responseData === 'object') {
                webhookResponse = responseData.output || responseData.message || responseData.text || responseData.response || '';
              } else if (typeof responseData === 'string') {
                webhookResponse = responseData;
              }
              
              if (webhookResponse) {
                console.log('Extracted webhook response:', webhookResponse);
                return webhookResponse;
              }
            } catch (jsonError) {
              console.error('JSON parsing error:', jsonError);
              // Even if we can't parse JSON, try to use the raw text as transcript
              return responseText;
            }
          }
          
          return '';
        } catch (error) {
          console.error('Error sending webhook request:', error);
          return '';
        }
      };
      
      // Prepare video context for the API
      const videoContext: {
        videoId: string | undefined;
        title: string;
        description: string;
        uploader: string;
        channel: string;
        publishDate: string;
        views: number;
        likes: number;
        instance: string;
        instanceUrl: string;
        transcript?: string; // Make transcript optional
      } = {
        videoId: video.uuid,
        title: video.name,
        description: video.description || '',
        uploader: video.account?.displayName || '',
        channel: video.channel?.displayName || '',
        publishDate: new Date().toISOString(),
        views: video.views || 0,
        likes: video.likes || 0,
        instance: selectedInstance.name,
        instanceUrl: selectedInstance.url
      };
      
      // Get the webhook response first, then send the question to the API
      try {
        // Start getting the webhook response, but don't wait too long
        const webhookResponsePromise = sendWebhookRequest();
        
        // Set a timeout to continue even if the webhook is taking too long
        const webhookTimeout = new Promise<string>(resolve => {
          setTimeout(() => resolve(''), 5000); // Wait max 5 seconds for webhook
        });
        
        // Race between the webhook response and the timeout
        const webhookResponse = await Promise.race([webhookResponsePromise, webhookTimeout]);
        
        // Update the videoContext with the transcript if we got one
        if (webhookResponse) {
          videoContext.transcript = webhookResponse;
          console.log('Added webhook response to videoContext:', webhookResponse);
        } else {
          console.log('No webhook response received within timeout, continuing without it');
        }
      } catch (error) {
        console.error('Error processing webhook:', error);
        // Continue without webhook response
      }

      try {
        // Check if we have a valid chat ID
        if (!chatId) {
          throw new Error('No chat ID available');
        }
        
        // Start streaming the question to the API immediately
        // The videoContext will be updated with the transcript when the webhook responds
        await streamQuestion(chatId, currentInput, videoContext, handleStreamChunk);
        
        // Mark the message as no longer streaming when done
        setMessages(prev => {
          return prev.map(msg => {
            if (msg.id === messageId) {
              return {
                ...msg,
                isStreaming: false
              };
            }
            return msg;
          });
        });
      } catch (streamingError) {
        console.error('Streaming error:', streamingError);
        
        // Show error in the message
        setMessages(prev => {
          return prev.map(msg => {
            if (msg.id === messageId) {
              return {
                ...msg,
                text: 'Fehler beim Streamen der Antwort. Bitte versuchen Sie es später erneut.',
                isStreaming: false
              };
            }
            return msg;
          });
        });
      }
    } catch (error) {
      console.error('Chat request error:', error);
      
      // Remove typing indicator first
      setMessages(prev => prev.filter(msg => msg.id !== typingIndicatorId));
      
      // Add appropriate error message with more details in development
      const errorMessage: ChatMessage = {
        id: Date.now() + 1,
        text: 'Entschuldigung, ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      // Always reset sending state
      setSendingMessage(false);
      
      // Ensure any lingering intervals are cleared
      const cleanupIntervals = () => {
        // This helps clean up any potentially leaked intervals
        const highestId = window.setTimeout(() => {}, 0);
        for (let i = highestId; i >= 0; i--) {
          window.clearInterval(i);
        }
      };
      
      // Only in error cases, do a more aggressive cleanup
      if (messages.some(msg => msg.text.includes('Fehler ist aufgetreten'))) {
        cleanupIntervals();
      }
    }
  }, [inputMessage, sendingMessage, video]);

  return (
    <Box sx={{ 
      height: '100%',
      width: '100%',
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: '#f5f5f5',
      overflow: 'hidden',
      position: 'relative',
      zIndex: 0, // Ensure this is below the header's z-index (1100)
      pt: isMobile ? '60px' : 0 // Add padding top for iOS to prevent header overlap
    }}>
      {/* Main content */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        overflow: 'hidden',
        height: isMobile ? 'auto' : '100%'
      }}>
        {/* Left container - Video and Info */}
        <Box sx={{
          width: isMobile ? '100%' : '50%', // 50% width on desktop as per user preference
          height: isMobile ? 'auto' : '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
          overflow: 'auto', // Allow scrolling for video info
          pb: isMobile ? 2 : 0 // Add padding at the bottom for mobile
        }}>
          {/* Video iframe */}
          <Box sx={{
            position: 'relative',
            paddingBottom: '56.25%', // 16:9 aspect ratio
            height: 0,
            overflow: 'hidden',
            width: '100%',
            backgroundColor: '#000',
            flexShrink: 0 // Prevent video from shrinking
          }}>
            <iframe
              ref={iframeRef}
              src={getVideoEmbedUrl(selectedInstance.url, video.uuid || '')}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              allowFullScreen={true}
              title={video.name}
            />
          </Box>
          
          {/* Video info - Below the video */}
          <Box sx={{
            padding: isMobile ? 2 : 2,
            flexShrink: 0, // Prevent info from shrinking
            display: 'flex',
            flexDirection: 'column',
            mb: isMobile ? 0 : 2 // No margin bottom on mobile
          }}>
            {/* Video title - Smaller on mobile */}
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.8rem' },
                mb: 1 // Small margin below title
              }}
            >
              {video.name}
            </Typography>
            
            {/* Stats section - Hidden in mobile view */}
            {!isMobile && <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2,
              pb: 2,
              borderBottom: '1px solid rgba(0, 162, 120, 0.2)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 0 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mr: 3,
                  color: 'text.secondary'
                }}>
                  <VisibilityIcon sx={{ fontSize: 20, mr: 0.8, color: '#00a278', opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {video.views ? video.views.toLocaleString() : '0'} Aufrufe
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  color: 'text.secondary'
                }}>
                  <ThumbUpIcon sx={{ fontSize: 20, mr: 0.8, color: '#00a278', opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {video.likes ? video.likes.toLocaleString() : '0'} Likes
                  </Typography>
                </Box>
              </Box>
              
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '4px',
                  fontWeight: 500
                }}
              >
                Veröffentlicht: {new Date(video.createdAt || Date.now()).toLocaleDateString('de-DE', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </Typography>
            </Box>
          }
            {/* Channel info - Hidden in mobile view */}
            {!isMobile && video.channel && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: 'rgba(0,0,0,0.02)'
              }}>
                {video.channel.avatarPath && (
                  <Avatar 
                    src={getFullThumbnailUrl(video.channel.avatarPath, selectedInstance.url)}
                    alt={video.channel.displayName || ''}
                    sx={{ 
                      width: 48, 
                      height: 48, 
                      mr: 2,
                      border: '2px solid #00a278',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    {video.channel.displayName?.charAt(0)}
                  </Avatar>
                )}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {video.channel.displayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {video.channel.followersCount ? `${video.channel.followersCount.toLocaleString()} Follower` : ''}
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Video description - Hidden in mobile view */}
            {!isMobile && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ mt: 2 }}>
                  <Box 
                    onClick={() => setInfoExpanded(!infoExpanded)}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      mb: 1,
                      p: 1,
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.02)'
                      }
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Beschreibung
                    </Typography>
                    <ExpandMoreIcon 
                      fontSize="small" 
                      sx={{ 
                        transform: infoExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                      }} 
                    />
                  </Box>
                  
                  <Collapse in={infoExpanded}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                      {video.description || 'Keine Beschreibung verfügbar.'}
                    </Typography>
                  </Collapse>
                </Box>
              </>
            )}
          </Box>
        </Box>
        
        {/* Right container - Chat */}
        <Box sx={{
          width: isMobile ? '100%' : '50%',
          height: '100%', // Always full height
          display: 'flex',
          flexDirection: 'column',
          borderLeft: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
          borderTop: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
          backgroundColor: '#f8f9fa',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: isMobile ? 'none' : '-4px 0 12px rgba(0,0,0,0.05)',
          flex: isMobile ? 1 : 'none' // Make it fill available space in mobile
        }}>
          {/* Chat header */}
          <Box sx={{
            p: 2, 
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar 
                src={tubeAILogo} 
                alt="TubeAI" 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  mr: 1,
                  backgroundColor: 'white',
                  p: 0.5,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }} 
              />
              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                Video-Assistent
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BucketButton style={{ marginRight: '8px' }} />
              <Tooltip title="Über den Video-Assistenten">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Messages - Full height with scrolling */}
          {(chatExpanded || !isMobile) && (
            <Box sx={{
              flex: 1,
              overflowY: 'auto',
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.7), rgba(255,255,255,0.7)), url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%23e0e0e0\' fill-opacity=\'0.2\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
              backgroundSize: '150px 150px',
              height: isMobile ? 'calc(100vh - 170px)' : 'auto' // Fixed height for mobile to fill available space
            }}>
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 2.5,
                  position: 'relative'
                }}
              >
                {message.sender === 'bot' && (
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      mr: 1,
                      bgcolor: '#00a278',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      padding: '5px',
                      '& img': { width: '100%', height: '100%' }
                    }}
                  >
                    <img src={tubeAILogo} alt="AI" style={{ width: '100%', height: '100%' }} />
                  </Avatar>
                )}
                <Box
                  sx={{
                    maxWidth: '75%',
                    padding: '12px 16px',
                    borderRadius: message.sender === 'user' 
                      ? '18px 4px 18px 18px' 
                      : '4px 18px 18px 18px',
                    backgroundColor: message.sender === 'user' 
                      ? '#00a278' 
                      : 'white',
                    color: message.sender === 'user' 
                      ? 'white' 
                      : 'text.primary',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                    position: 'relative',
                    '&::before': message.sender === 'bot' ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: -8,
                      width: 0,
                      height: 0,
                      borderTop: '8px solid white',
                      borderLeft: '8px solid transparent',
                    } : message.sender === 'user' ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: -8,
                      width: 0,
                      height: 0,
                      borderTop: '8px solid',
                      borderTopColor: '#00a278',
                      borderRight: '8px solid transparent',
                    } : {}
                  }}
                >
                  {message.isTypingIndicator ? (
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  ) : (
                    <Box sx={{ 
                      '& p': { m: 0 },
                      '& a': { color: message.sender === 'user' ? 'white' : 'primary.main', fontWeight: 500 },
                      '& code': { 
                        backgroundColor: message.sender === 'user' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.04)',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontSize: '0.9em'
                      }
                    }}>
                      <div
                        ref={(el) => {
                          if (el && philaMarkdownRef.current && message.text) {
                            // Render markdown content using Phila Markdown
                            philaMarkdownRef.current.renderToElement(message.text, el);
                          }
                        }}
                      />
                      {message.sender === 'bot' && !message.isTypingIndicator && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                          <SaveMessageButton message={message} videoInfo={video} />
                        </Box>
                      )}
                    </Box>
                  )}
                  {message.isStreaming && (
                    <span className="streaming-cursor">|</span>
                  )}
                  <Typography variant="caption" sx={{ 
                    display: 'block',
                    textAlign: 'right',
                    marginTop: 1,
                    opacity: 0.7,
                    fontSize: '0.7rem',
                    color: message.sender === 'user' 
                      ? 'rgba(255, 255, 255, 0.8)' 
                      : 'rgba(0, 0, 0, 0.5)'
                  }}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
                {message.sender === 'user' && (
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      ml: 1,
                      bgcolor: 'grey.300',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    <PersonIcon fontSize="small" />
                  </Avatar>
                )}
              </Box>
            ))}
            <Box sx={{ marginTop: 'auto' }} ref={messagesEndRef} />
          </Box>
          )}
          {/* Input - Always visible */}
          <Box sx={{
            p: 2, 
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'white',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.05)'
          }}>
            <Box sx={{ 
              display: 'flex',
              position: 'relative'
            }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Nachricht eingeben..."
                value={inputMessage}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                disabled={sendingMessage}
                sx={{ 
                  mr: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '24px',
                    backgroundColor: '#f5f5f5',
                    transition: 'all 0.3s ease',
                    pr: 6,
                    '&:hover, &.Mui-focused': {
                      backgroundColor: '#fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0,0,0,0.1)'
                  },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: '1px'
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={sendingMessage || !inputMessage.trim()}
                sx={{ 
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: '#00a278',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#008e6a',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(0,0,0,0.12)',
                    color: 'rgba(0,0,0,0.26)'
                  },
                  width: 40,
                  height: 40
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Global styles */}
      <GlobalStyles
        styles={{
          body: {
            margin: 0,
            padding: 0,
            overflow: 'hidden',
          },
          '.typing-indicator': {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            padding: '6px 0',
          },
          '.typing-indicator span': {
            width: '8px',
            height: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'typing 1.4s infinite ease-in-out both',
          },
          '.typing-indicator span:nth-of-type(1)': {
            animationDelay: '0s',
          },
          '.typing-indicator span:nth-of-type(2)': {
            animationDelay: '0.2s',
          },
          '.typing-indicator span:nth-of-type(3)': {
            animationDelay: '0.4s',
          },
          '.streaming-cursor': {
            display: 'inline-block',
            marginLeft: '2px',
            fontWeight: 'normal',
            animation: 'blink 0.7s infinite',
            color: 'rgba(0, 0, 0, 0.7)',
          },
          '@keyframes typing': {
            '0%, 60%, 100%': { transform: 'translateY(0)' },
            '30%': { transform: 'translateY(-6px)' },
          },
          '@keyframes blink': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0 },
          }
        }}
      />
    </Box>
  );
};

export default VideoPlayer;
